/**
 * Settings-panel open-state store — the client-side "is the Settings panel
 * open?" value driving the right-docked Settings drawer (ADR-033 §5;
 * REQ-UI-076).
 *
 * # The new model (architect 2026-06-08, part 1)
 *
 * Settings are **always live**: the panel edits the live working state in
 * place (the operator's environment), and a change persists immediately to
 * preferences. There is no draft / preview / cancel and no New/Edit *session*
 * — the panel is "Settings", not a per-Profile editor. Saving snapshots the
 * current working state (incl. the live layout + viewport) into a Profile,
 * either as a new one or as an update to the active one.
 *
 * So this store collapses from the previous New/Edit discriminated-union
 * session to a single boolean: **is the panel open?**.
 *
 * # Why a store (not a prop / context)
 *
 * The trigger (the top-bar gear menu in ``preferences-menu.tsx``) and the
 * mount point (the Settings drawer in ``app-shell.tsx``) live in different
 * subtrees: the gear is always-present chrome; the drawer overlays the
 * canvas at the right edge. A module-level Zustand store (REQ-UI-027) is
 * the smallest tool that lets the gear *open* the panel and the drawer
 * *render* it without threading a callback through the whole tree — the
 * same posture as the selection bus (``selection-store.ts``). The open flag
 * is pure client UI state (it never round-trips to the BFF), so Zustand is
 * the right home per REQ-UI-027 (server state stays in TanStack Query / the
 * preferences working-state writer).
 *
 * Cross-references:
 *   - REQ-UI-076 (Settings panel — non-blocking right drawer).
 *   - REQ-UI-027 (Zustand for client UI state).
 *   - ADR-033 §5 (the editor + layout-capture design; part 1 reframes it as
 *     "Settings" editing the live working state).
 *   - profiles/profile-editor.tsx (the drawer that subscribes).
 */
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

/**
 * The Settings-panel store's shape. Actions sit alongside state (Zustand
 * idiom); the action references are stable across renders so they're safe in
 * effect dependency arrays.
 */
export interface ProfileEditorState {
  /** ``true`` while the Settings panel is open (the drawer renders). */
  readonly isOpen: boolean;
  /** Open the Settings panel. Idempotent. */
  readonly open: () => void;
  /** Close the Settings panel (the drawer unmounts). Idempotent. */
  readonly close: () => void;
}

/**
 * The module-level Settings-panel store. Components subscribe via the narrow
 * helper hooks below; non-React write sites (none today) would use
 * ``useProfileEditorStore.getState().open()``.
 */
export const useProfileEditorStore = create<ProfileEditorState>((set) => ({
  isOpen: false,
  open: () => {
    set({ isOpen: true });
  },
  close: () => {
    set({ isOpen: false });
  },
}));

/**
 * Subscribe to whether the Settings panel is open. The drawer reads this to
 * decide whether to render. Re-renders only when the flag flips.
 */
export function useSettingsPanelOpen(): boolean {
  return useProfileEditorStore((state) => state.isOpen);
}

/**
 * The panel's open/close actions, as a stable-reference object. The gear menu
 * destructures ``{ open }``; the drawer uses ``close``.
 *
 * ``useShallow`` keeps the returned object reference stable so consumers
 * don't re-subscribe every render (Zustand v5 reference-equality default
 * would otherwise observe a fresh object each render).
 */
export function useProfileEditorActions(): {
  open: ProfileEditorState["open"];
  close: ProfileEditorState["close"];
} {
  return useProfileEditorStore(
    useShallow((state) => ({
      open: state.open,
      close: state.close,
    })),
  );
}
